import { Link } from "@tanstack/react-router";
import { ArrowDownCircleIcon, CircleDashedIcon } from "lucide-react";

import { trpc } from "@blis/trpc/react";

import { SubHeader } from "@/components/SubHeader";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import type { SubjectRouter, TSubjectDetail } from "@/lib/types";
import { format } from "@/lib/utils";
import { MultiColumnView } from "./SubjectMultiColumn";
import { useSubject } from "./SubjectProvider";

export function SubjectSamplesView({
  sampleId,
}: {
  sampleId: number | undefined;
}) {
  const { subject } = useSubject();
  const samples = trpc.subjects.samples.useQuery({ subjectId: subject.id });

  const selectedSample = samples.data?.find((s) => s.id === sampleId);
  const isLoadingOrNotEmpty =
    samples.isLoading || (samples.data && samples.data.length > 0);

  return (
    <MultiColumnView
      isDetailView={!!sampleId}
      nav={
        <NavList
          items={samples.data || []}
          autoFocus
          className="divide-y"
          isLoading={samples.isPending}
          loadingItems={5}
          LoadingComponent={() => (
            <div className="flex flex-col gap-2 px-4 h-20 justify-center ">
              <Skeleton className="w-20" />
              <Skeleton className="w-24" />
            </div>
          )}
        >
          {(sample) => (
            <NavListItem key={sample.id} asChild>
              <SampleViewItem sample={sample} subject={subject} />
            </NavListItem>
          )}
        </NavList>
      }
    >
      {selectedSample ? (
        <>
          <SubHeader>
            <SubHeader.Left>
              <Breadcrumbs>
                {/* <Breadcrumb
                  href={{
                    to: "/subjects/$id/samples",
                    params: { id: subject.id },
                  }}
                >
                  Samples
                </Breadcrumb> */}
                <Breadcrumb>{selectedSample.displayId}</Breadcrumb>
              </Breadcrumbs>
            </SubHeader.Left>
          </SubHeader>
          <div className="flex-1 flex flex-col overflow-auto">
            <div className="p-4">
              <h1 className="text-2xl font-bold">{selectedSample.displayId}</h1>
            </div>
          </div>
        </>
      ) : (
        <div>No sample selected</div>
      )}
    </MultiColumnView>
  );
}

function SampleViewItem({
  sample,
  subject,
}: {
  sample: SubjectRouter["samples"][0];
  subject: TSubjectDetail;
}) {
  return (
    <Link
      className="flex flex-col gap-2 p-4 outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring focus-visible:rounded"
      to="/subjects/$id/samples"
      params={{ id: subject.id }}
      activeProps={{ className: "bg-accent-muted" }}
      preload={false}
      replace
      search={(prev) => ({ ...prev, sampleId: sample.id })}
    >
      <div className="w-full">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-1">
            {sample.received ? (
              <ArrowDownCircleIcon className="size-3" />
            ) : (
              <CircleDashedIcon className="size-3" />
            )}
            <Badge size="sm" variant="outline" className="font-mono shrink-0">
              {sample.displayId}
            </Badge>
          </div>
          <Text mono muted size="xs">
            {sample.collection ? format(sample.collection) : ""}
          </Text>
        </div>
        <Text className="truncate block">{sample.tube.name}</Text>
      </div>
    </Link>
  );
}
